import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Loader2, Plus, Trash2, X, Check, Eye, FileText, ClipboardList, Download } from 'lucide-react';
import { API_URL, downloadPdfWithAuth } from './shared';

const FormsTab = ({ token }) => {
  const [medicalForms, setMedicalForms] = useState([]);
  const [liabilityWaivers, setLiabilityWaivers] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formModalType, setFormModalType] = useState('medical');

  useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [medicalRes, waiversRes] = await Promise.all([
        axios.get(`${API_URL}/admin/medical-forms`, { headers }),
        axios.get(`${API_URL}/admin/liability-waivers`, { headers })
      ]);
      setMedicalForms(medicalRes.data);
      setLiabilityWaivers(waiversRes.data);
    } catch (error) {
      toast.error('Failed to load forms');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Completed Forms</h2>
        <div className="flex items-center gap-4">
          <div className="flex gap-2 text-sm text-gray-500">
            <span>{medicalForms.length} Medical Forms</span>
            <span>-</span>
            <span>{liabilityWaivers.length} Liability Waivers</span>
          </div>
          <Button 
            variant="outline"
            onClick={() => {
              const allForms = { exported_at: new Date().toISOString(), medical_forms: medicalForms, liability_waivers: liabilityWaivers };
              const content = JSON.stringify(allForms, null, 2);
              const blob = new Blob([content], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `all-forms-${new Date().toISOString().split('T')[0]}.json`;
              a.click();
              URL.revokeObjectURL(url);
              toast.success('All forms downloaded');
            }}
            disabled={medicalForms.length === 0 && liabilityWaivers.length === 0}
          >
            <Download className="w-4 h-4 mr-2" />
            Download All Forms
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Medical Questionnaires */}
        <div className="bg-white rounded-lg border">
          <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
            <h3 className="font-semibold text-[#0A2342] flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#FF6B35]" />
              Medical Questionnaires
            </h3>
            {medicalForms.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => {
                const content = JSON.stringify(medicalForms, null, 2);
                const blob = new Blob([content], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `medical-forms-${new Date().toISOString().split('T')[0]}.json`;
                a.click();
                URL.revokeObjectURL(url);
                toast.success('Medical forms downloaded');
              }}>
                <Download className="w-4 h-4" />
              </Button>
            )}
          </div>
          <div className="max-h-[500px] overflow-y-auto">
            {medicalForms.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No medical questionnaires submitted yet</p>
              </div>
            ) : (
              <div className="divide-y">
                {medicalForms.map((form, idx) => (
                  <div key={form.id || idx} className="p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{form.participant_name || form.participantName || form.name || 'Unknown'}</p>
                        <p className="text-sm text-gray-500">{form.email || form.participantEmail || 'No email'}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {form.created_at ? new Date(form.created_at).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Date unknown'}
                        </p>
                        {form.trip_name && <p className="text-xs text-[#FF6B35] mt-1">Trip: {form.trip_name}</p>}
                        {(form.signature || form.signatureName || form.signature_name) && <Badge className="bg-green-100 text-green-700 mt-1">Signed</Badge>}
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => { setSelectedForm(form); setFormModalType('medical'); setFormModalOpen(true); }} title="View Document" data-testid={`view-medical-form-${form.id}`}>
                          <Eye className="w-4 h-4 mr-1" /><span className="hidden sm:inline">View</span>
                        </Button>
                        <Button variant="outline" size="sm" className="text-[#FF6B35] border-[#FF6B35] hover:bg-[#FF6B35] hover:text-white" onClick={() => downloadPdfWithAuth(`${API_URL}/admin/medical-forms/${form.id}/pdf`, `medical-form-${form.participant_name || form.id}.pdf`, token)} title="Download PDF">
                          <FileText className="w-4 h-4 mr-1" /><span className="hidden sm:inline">PDF</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Liability Waivers */}
        <div className="bg-white rounded-lg border">
          <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
            <h3 className="font-semibold text-[#0A2342] flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-[#FF6B35]" />
              Liability Waivers
            </h3>
            {liabilityWaivers.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => {
                const content = JSON.stringify(liabilityWaivers, null, 2);
                const blob = new Blob([content], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `liability-waivers-${new Date().toISOString().split('T')[0]}.json`;
                a.click();
                URL.revokeObjectURL(url);
                toast.success('Liability waivers downloaded');
              }}>
                <Download className="w-4 h-4" />
              </Button>
            )}
          </div>
          <div className="max-h-[500px] overflow-y-auto">
            {liabilityWaivers.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <ClipboardList className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No liability waivers submitted yet</p>
              </div>
            ) : (
              <div className="divide-y">
                {liabilityWaivers.map((waiver, idx) => (
                  <div key={waiver.id || idx} className="p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{waiver.participant_name || waiver.participantName || waiver.name || 'Unknown'}</p>
                        <p className="text-sm text-gray-500">{waiver.email || waiver.participantEmail || 'No email'}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {waiver.created_at ? new Date(waiver.created_at).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Date unknown'}
                        </p>
                        {waiver.trip_name && <p className="text-xs text-[#FF6B35] mt-1">Trip: {waiver.trip_name}</p>}
                        {(waiver.accepted || waiver.waiverAccepted) && <Badge className="bg-green-100 text-green-700 mt-1">Signed</Badge>}
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => { setSelectedForm(waiver); setFormModalType('waiver'); setFormModalOpen(true); }} title="View Document" data-testid={`view-waiver-${waiver.id}`}>
                          <Eye className="w-4 h-4 mr-1" /><span className="hidden sm:inline">View</span>
                        </Button>
                        <Button variant="outline" size="sm" className="text-[#FF6B35] border-[#FF6B35] hover:bg-[#FF6B35] hover:text-white" onClick={() => downloadPdfWithAuth(`${API_URL}/admin/liability-waivers/${waiver.id}/pdf`, `liability-waiver-${waiver.participant_name || waiver.id}.pdf`, token)} title="Download PDF">
                          <FileText className="w-4 h-4 mr-1" /><span className="hidden sm:inline">PDF</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Form Detail Modal - Document View */}
      {formModalOpen && selectedForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b flex justify-between items-center bg-[#0A2342]">
              <h3 className="font-semibold text-white">
                {formModalType === 'medical' ? 'Medical Questionnaire' : 'Liability Waiver'} - Document View
              </h3>
              <button onClick={() => setFormModalOpen(false)} className="text-white hover:text-gray-300">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)] bg-gray-50">
              <div className="bg-white rounded-lg shadow-lg p-8 max-w-2xl mx-auto">
                {/* Header */}
                <div className="text-center mb-6 border-b pb-4">
                  <h1 className="text-2xl font-bold text-[#0A2342]">JUST DIVE</h1>
                  <p className="text-[#FF6B35] font-medium">
                    {formModalType === 'medical' ? 'Medical Questionnaire' : 'Liability Waiver & Release Form'}
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    Submitted: {selectedForm.created_at ? new Date(selectedForm.created_at).toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                  </p>
                </div>

                {/* Participant Information */}
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-[#0A2342] mb-3 border-b pb-1">Participant Information</h2>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-gray-500">Full Name:</span><p className="font-medium">{selectedForm.participant_name || selectedForm.participantName || selectedForm.name || 'N/A'}</p></div>
                    <div><span className="text-gray-500">Email:</span><p className="font-medium">{selectedForm.email || selectedForm.participantEmail || 'N/A'}</p></div>
                    <div><span className="text-gray-500">Phone:</span><p className="font-medium">{selectedForm.phone || 'N/A'}</p></div>
                    <div><span className="text-gray-500">Date of Birth:</span><p className="font-medium">{selectedForm.date_of_birth || selectedForm.birthdate || 'N/A'}</p></div>
                    <div><span className="text-gray-500">Emergency Contact:</span><p className="font-medium">{selectedForm.emergency_contact || selectedForm.emergencyContact || 'N/A'}</p></div>
                    <div><span className="text-gray-500">Emergency Phone:</span><p className="font-medium">{selectedForm.emergency_phone || selectedForm.emergencyPhone || 'N/A'}</p></div>
                    {(selectedForm.trip_name || selectedForm.tripName) && (
                      <div className="col-span-2"><span className="text-gray-500">Associated Trip:</span><p className="font-medium text-[#FF6B35]">{selectedForm.trip_name || selectedForm.tripName}</p></div>
                    )}
                  </div>
                </div>

                {/* Medical Responses */}
                {formModalType === 'medical' && (selectedForm.responses || selectedForm.medical) && (
                  <div className="mb-6">
                    <h2 className="text-lg font-semibold text-[#0A2342] mb-3 border-b pb-1">Medical History</h2>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      {Object.entries(selectedForm.responses || selectedForm.medical || {}).map(([key, value]) => (
                        <div key={key} className="flex justify-between text-sm border-b border-gray-200 pb-1 last:border-0">
                          <span className="text-gray-600">{key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim()}:</span>
                          <span className={`font-medium ${value === true || value === 'yes' ? 'text-red-600' : 'text-green-600'}`}>
                            {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Signature Section */}
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-[#0A2342] mb-3 border-b pb-1">Declaration & Signature</h2>
                  <div className="flex items-center gap-2 mb-4">
                    {(selectedForm.signature || selectedForm.signatureName || selectedForm.signature_name || selectedForm.waiverAccepted || selectedForm.signature_image) ? (
                      <><Check className="w-5 h-5 text-green-600" /><span className="text-green-600 font-semibold">SIGNED & ACCEPTED</span></>
                    ) : (
                      <><X className="w-5 h-5 text-red-600" /><span className="text-red-600 font-semibold">NOT SIGNED</span></>
                    )}
                  </div>
                  {selectedForm.signature_image && (
                    <div className="mb-4">
                      <p className="text-sm text-gray-500 mb-2">Signature ({selectedForm.signature_type === 'type' ? 'Typed' : 'Drawn'}):</p>
                      <div className="border-2 border-gray-200 rounded-lg p-2 bg-white inline-block">
                        <img src={selectedForm.signature_image} alt="Signature" className="max-w-[300px] h-auto" />
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {(selectedForm.signature_name || selectedForm.signatureName) && (
                      <div><span className="text-gray-500">Signature Name:</span><p className="font-medium italic">{selectedForm.signature_name || selectedForm.signatureName}</p></div>
                    )}
                    <div><span className="text-gray-500">Signature Date:</span><p className="font-medium">{selectedForm.signature_date || selectedForm.signatureDate || 'N/A'}</p></div>
                  </div>
                </div>

                {/* Immutability Notice */}
                <div className="text-center text-xs text-gray-400 border-t pt-4 mt-6">
                  <p>This document is an immutable record of the submission.</p>
                  <p>Document ID: {selectedForm.id}</p>
                </div>
              </div>
            </div>
            <div className="p-4 border-t bg-white flex justify-end gap-2">
              <Button variant="outline" className="text-[#FF6B35] border-[#FF6B35] hover:bg-[#FF6B35] hover:text-white" onClick={() => {
                const url = formModalType === 'medical' ? `${API_URL}/admin/medical-forms/${selectedForm.id}/pdf` : `${API_URL}/admin/liability-waivers/${selectedForm.id}/pdf`;
                const filename = formModalType === 'medical' ? `medical-form-${selectedForm.participant_name || selectedForm.id}.pdf` : `liability-waiver-${selectedForm.participant_name || selectedForm.id}.pdf`;
                downloadPdfWithAuth(url, filename, token);
              }} data-testid="download-form-pdf">
                <FileText className="w-4 h-4 mr-2" />Download PDF
              </Button>
              <Button onClick={() => setFormModalOpen(false)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormsTab;
